import { LoadingSpinner } from '@/components/ui/spinner';
import { useData } from '@/context/data.provider';
import { useUserInfo } from '@/context/user-info';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface PageProps {
  isLoading?: boolean;
  title?: string;
  description?: string;
  keywords?: string;
  jsonLd?: object;
  children: React.ReactNode;
}

export function Page({
  isLoading,
  title,
  description,
  keywords,
  jsonLd,
  children,
}: PageProps) {
  const location = useLocation();
  const { userLanguage } = useUserInfo();
  const { cities } = useData();

  const pathname = location.pathname;
  const citySlug = pathname.split('/').pop()?.toLowerCase();
  const city = cities?.find((c) => c.name.toLowerCase() === citySlug);

  // Fallback SEO content
  const generatedTitle =
    title ??
    {
      es: city
        ? `Descubre YEGO en ${city.name}`
        : 'YEGO – Motos eléctricas para moverte por tu ciudad',
      fr: city
        ? `Découvrez YEGO à ${city.name}`
        : 'YEGO – Scooters électriques en libre service',
      en: city
        ? `Discover YEGO in ${city.name}`
        : 'YEGO – Electric scooters to move around your city',
      ca: city
        ? `Descobreix YEGO a ${city.name}`
        : 'YEGO – Motos elèctriques compartides',
      it: city
        ? `Scopri YEGO a ${city.name}`
        : 'YEGO – Scooter elettrici in sharing per muoverti in città',
    }[userLanguage ?? 'en'];

  const generatedDescription =
    description ??
    {
      es: city
        ? `Aprende cómo moverte por ${city.name} con las motos eléctricas compartidas de YEGO.`
        : 'Muévete con motos eléctricas en tu ciudad de forma rápida y sostenible.',
      fr: city
        ? `Apprenez à vous déplacer à ${city.name} avec les scooters YEGO.`
        : 'Déplacez-vous avec des scooters électriques dans votre ville.',
      en: city
        ? `Learn how to ride in ${city.name} with YEGO electric scooters.`
        : 'Ride electric scooters in your city with YEGO.',
      ca: city
        ? `Aprèn com moure’t per ${city.name} amb les motos elèctriques de YEGO.`
        : 'Mou-te de forma sostenible amb motos elèctriques compartides.',
      it: city
        ? `Scopri come muoverti a ${city.name} con gli scooter elettrici YEGO.`
        : 'Muoviti con gli scooter elettrici nella tua città con YEGO.',
    }[userLanguage ?? 'en'];

  const url = `https://rideyego.com${pathname}`;

  const alternateLangs = ['en', 'es', 'fr', 'ca'];

  return (
    <>
      <Helmet>
        <title>{generatedTitle}</title>
        <meta name="description" content={generatedDescription} />
        <link rel="manifest" href="/manifest.json" />
        {keywords && <meta name="keywords" content={keywords} />}
        <meta property="og:title" content={generatedTitle} />
        <meta property="og:description" content={generatedDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />

        {jsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(jsonLd, null, 2)}
          </script>
        )}

        <link
          rel="preload"
          as="font"
          href="/fonts/phosphate-solid.woff2"
          type="font/woff2"
          crossOrigin="anonymous"
        />

        {alternateLangs.map((lang) => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={`https://rideyego.com/${lang}${pathname}`}
          />
        ))}
      </Helmet>

      {/* Negative margin to position it under the navbar */}
      {isLoading ? (
        <div className="h-screen w-full">
          <LoadingSpinner className="size-16 flex items-center justify-center" />
        </div>
      ) : (
        <div className="-mt-[84px]">{children}</div>
      )}
    </>
  );
}
